import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, UserIcon, TagIcon, ArrowRightIcon, BookOpenIcon, SearchIcon, ChevronRightIcon, AlertCircleIcon, FilterIcon } from 'lucide-react';
import { BlogPost as BlogPostType, sampleBlogPosts } from '../data/sampleBlogPosts';
export function Blog() {
  const [blogPosts, setBlogPosts] = useState<BlogPostType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const categories = ['Business', 'News', 'Sports', 'Storytelling'];
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await fetch('http://localhost:3001/posts', {
          // Add a timeout to prevent long loading times if the API is down
          signal: AbortSignal.timeout(5000)
        });
        if (!response.ok) {
          throw new Error('Failed to fetch blog posts');
        }
        const data = await response.json();
        // Combine fetched posts with sample posts to ensure content
        // Use a Map to prevent duplicates by ID
        const postsMap = new Map<string, BlogPostType>();
        // Add sample posts first (they'll be overwritten by API posts with same ID)
        sampleBlogPosts.forEach(post => postsMap.set(post.id, post));
        // Add API posts
        data.forEach((post: BlogPostType) => postsMap.set(post.id, post));
        setBlogPosts(Array.from(postsMap.values()));
      } catch (err: any) {
        console.error('Error fetching blog posts:', err);
        // Silently fall back to sample posts without showing an error message
        setBlogPosts(sampleBlogPosts);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);
  // Filter posts by search term and category
  const filteredPosts = blogPosts.filter(post => (post.title.toLowerCase().includes(searchTerm.toLowerCase()) || post.excerpt.toLowerCase().includes(searchTerm.toLowerCase())) && (selectedCategory === '' || post.category === selectedCategory));
  // Count posts by category
  const getCategoryCount = (category: string) => {
    return blogPosts.filter(post => post.category === category).length;
  };
  return <div className="w-full bg-white" data-id="element-992">
      {/* Header Section */}
      <section className="cream-bg py-16 w-full" data-id="element-993">
        <div className="container mx-auto px-4" data-id="element-994">
          <div className="max-w-4xl mx-auto text-center" data-id="element-995">
            <div className="flex justify-center mb-6" data-id="element-996">
              <div className="w-16 h-16 rounded-full gold-gradient flex items-center justify-center shadow-lg" data-id="element-997">
                <BookOpenIcon size={32} className="text-white" data-id="element-998" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 elegant-heading" data-id="element-999">
              Insights & Stories
            </h1>
            <div className="elegant-divider" data-id="element-1000"></div>
            <p className="text-xl navy-text max-w-2xl mx-auto mb-8" data-id="element-1001">
              Explore our collection of thoughts, strategies, and narratives on
              content creation, brand storytelling, and the evolving media
              landscape in East Africa.
            </p>
            <div className="relative max-w-xl mx-auto" data-id="element-1002">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1003">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-1004" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-4 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition duration-150 ease-in-out sm:text-sm" placeholder="Search articles..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1005" />
            </div>
          </div>
        </div>
      </section>
      {/* Categories Section */}
      <section className="border-b border-gray-200 py-6 bg-white" data-id="element-1006">
        <div className="container mx-auto px-4" data-id="element-1007">
          <div className="max-w-4xl mx-auto" data-id="element-1008">
            <div className="flex items-center mb-4" data-id="element-1009">
              <FilterIcon size={18} className="text-amber-600 mr-2" data-id="element-1010" />
              <h3 className="text-lg font-medium" data-id="element-1011">Browse by Category</h3>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-1012">
              <button onClick={() => setSelectedCategory('')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${selectedCategory === '' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`} data-id="element-1013">
                All Categories
                <span className="ml-2 text-xs opacity-80" data-id="element-1014">
                  ({blogPosts.length})
                </span>
              </button>
              {categories.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${selectedCategory === category ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`} data-id="element-1015">
                  {category}
                  <span className="ml-2 text-xs opacity-80" data-id="element-1016">
                    ({getCategoryCount(category)})
                  </span>
                </button>)}
            </div>
          </div>
        </div>
      </section>
      {/* Blog Posts List */}
      <section className="py-16 bg-white w-full" data-id="element-1017">
        <div className="container mx-auto px-4" data-id="element-1018">
          <div className="mb-8" data-id="element-1019">
            <h2 className="text-2xl font-bold elegant-heading" data-id="element-1020">
              {selectedCategory ? `${selectedCategory} Articles` : 'All Articles'}
            </h2>
            <div className="elegant-divider w-24 ml-0 mt-2" data-id="element-1021"></div>
          </div>
          {/* Error state - only show for errors that aren't related to API connectivity */}
          {error && <div className="max-w-4xl mx-auto p-4 bg-red-50 text-red-700 rounded-lg mb-8 flex items-center" data-id="element-1022">
              <AlertCircleIcon size={24} className="mr-3 flex-shrink-0" data-id="element-1023" />
              <p data-id="element-1024">{error}</p>
            </div>}
          {/* Loading state */}
          {loading ? <div className="flex justify-center py-20" data-id="element-1025">
              <div className="w-16 h-16 border-t-4 border-amber-600 border-solid rounded-full animate-spin" data-id="element-1026"></div>
            </div> : filteredPosts.length > 0 ? <div className="space-y-10 max-w-4xl mx-auto" data-id="element-1027">
              {filteredPosts.map(post => <div key={post.id} className="group relative overflow-hidden rounded-xl border-b border-gray-100 pb-10 last:border-0" data-id="element-1028">
                  {/* Decorative corner accent */}
                  <div className="absolute -right-4 -top-4 h-16 w-16 rotate-12 transform bg-amber-600 opacity-5 transition-all duration-500 group-hover:rotate-6 group-hover:opacity-10" data-id="element-1029"></div>
                  <div className="relative" data-id="element-1030">
                    <div className="mb-3 flex items-center" data-id="element-1031">
                      <span className="rounded-full bg-amber-600 bg-opacity-10 px-3 py-1 text-xs font-medium text-amber-700" data-id="element-1032">
                        {post.category}
                      </span>
                      <span className="ml-4 flex items-center text-xs text-gray-500" data-id="element-1033">
                        <CalendarIcon size={14} className="mr-1" data-id="element-1034" />
                        {post.date}
                      </span>
                      <span className="ml-4 flex items-center text-xs text-gray-500" data-id="element-1035">
                        <UserIcon size={14} className="mr-1" data-id="element-1036" />
                        {post.author}
                      </span>
                    </div>
                    <h3 className="mb-3 text-2xl font-bold transition-colors group-hover:text-amber-600" data-id="element-1037">
                      <Link to={`/blog/${post.id}`} className="flex items-start" data-id="element-1038">
                        <div className="mr-3 mt-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-50" data-id="element-1039">
                          <BookOpenIcon size={16} className="text-amber-600" data-id="element-1040" />
                        </div>
                        <span data-id="element-1041">{post.title}</span>
                      </Link>
                    </h3>
                    <p className="mb-4 ml-11 text-gray-700" data-id="element-1042">{post.excerpt}</p>
                    <div className="ml-11 flex items-center justify-between" data-id="element-1043">
                      <span className="text-sm text-gray-500" data-id="element-1044">
                        {post.readTime}
                      </span>
                      <Link to={`/blog/${post.id}`} className="flex items-center text-sm font-medium text-gray-900 transition-all group-hover:text-amber-600" data-id="element-1045">
                        <span data-id="element-1046">Read full article</span>
                        <ArrowRightIcon size={14} className="ml-1 transition-transform duration-300 group-hover:translate-x-1" data-id="element-1047" />
                      </Link>
                    </div>
                    {/* Animated line */}
                    <div className="absolute bottom-0 left-11 h-0.5 w-24 translate-y-5 bg-gradient-to-r from-amber-300 to-amber-600 opacity-0 transition-all duration-300 group-hover:opacity-100" data-id="element-1048"></div>
                  </div>
                </div>)}
            </div> : <div className="text-center py-16 max-w-4xl mx-auto" data-id="element-1049">
              <BookOpenIcon size={48} className="mx-auto text-gray-300 mb-4" data-id="element-1050" />
              <h3 className="text-xl font-medium text-gray-900 mb-2" data-id="element-1051">
                No articles found
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-1052">
                {searchTerm || selectedCategory ? `No articles matching your filters` : 'No articles available at the moment'}
              </p>
              <button onClick={() => {
            setSearchTerm('');
            setSelectedCategory('');
          }} className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-1053">
                View all articles
              </button>
            </div>}
        </div>
      </section>
      {/* Newsletter Section */}
      <section className="py-16 cream-bg w-full" data-id="element-1054">
        <div className="container mx-auto px-4" data-id="element-1055">
          <div className="max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-lg luxury-border" data-id="element-1056">
            <div className="text-center mb-6" data-id="element-1057">
              <h2 className="text-2xl font-bold mb-4 elegant-heading" data-id="element-1058">
                Join Our Newsletter
              </h2>
              <div className="elegant-divider" data-id="element-1059"></div>
              <p className="text-gray-700" data-id="element-1060">
                Get our latest insights on content strategy and brand
                storytelling delivered directly to your inbox.
              </p>
            </div>
            <form className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto" data-id="element-1061">
              <input type="email" placeholder="Your email address" className="flex-grow px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-500" required data-id="element-1062" />
              <button type="submit" className="gold-gradient text-white hover:opacity-90 px-6 py-3 rounded-md transition-opacity whitespace-nowrap" data-id="element-1063">
                Subscribe
              </button>
            </form>
            <p className="text-xs text-gray-500 text-center mt-4" data-id="element-1064">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>
    </div>;
}